import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Video, File } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';

interface FileUploadProps {
  type: 'image' | 'video';
  onUpload: (fileUrl: string) => void;
  onRemove?: () => void;
  currentUrl?: string;
  className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  type,
  onUpload,
  onRemove,
  currentUrl,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Валидация типа файла
    const isImage = type === 'image' && file.type.startsWith('image/');
    const isVideo = type === 'video' && file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error(`Пожалуйста, выберите ${type === 'image' ? 'изображение' : 'видео'} файл`);
      return;
    }

    // Валидация размера файла (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Файл слишком большой. Максимальный размер: 50MB');
      return;
    }

    setIsUploading(true);

    try {
      // Создаем предварительный просмотр
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);

      // Загружаем файл на сервер
      const uploadFunction = type === 'image' ? apiService.uploadImage : apiService.uploadVideo;
      const result = await uploadFunction(file);

      // Вызываем callback с URL загруженного файла
      onUpload(result.file_url);
      
      toast.success(`${type === 'image' ? 'Изображение' : 'Видео'} успешно загружено`);
    } catch (error: any) {
      console.error('Ошибка загрузки файла:', error);
      
      // Показываем более подробную информацию об ошибке
      const errorMessage = error.detail || error.message || 'Ошибка при загрузке файла';
      toast.error(errorMessage);
      
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
      // Очищаем input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onRemove?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const getIcon = () => {
    if (type === 'image') return <ImageIcon size={24} />;
    return <Video size={24} />;
  };

  const getPlaceholder = () => {
    if (type === 'image') {
      return 'Нажмите для загрузки изображения';
    }
    return 'Нажмите для загрузки видео';
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Скрытый input для выбора файла */}
      <input
        ref={fileInputRef}
        type="file"
        accept={type === 'image' ? 'image/*' : 'video/*'}
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Область загрузки */}
      <div
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors
          ${isUploading 
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }
        `}
      >
        {previewUrl ? (
          // Предварительный просмотр
          <div className="relative">
            {type === 'image' ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
              />
            ) : (
              <video
                src={previewUrl}
                controls
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
            
            {/* Кнопка удаления */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          // Плейсхолдер для загрузки
          <div className="text-center">
            {isUploading ? (
              <div className="space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-sm text-gray-600">Загрузка...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  {getIcon()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {getPlaceholder()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {type === 'image' 
                      ? 'JPEG, PNG, GIF, WebP до 50MB' 
                      : 'MP4, AVI, MOV, WMV, FLV, WebM до 50MB'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Информация о текущем файле */}
      {currentUrl && !previewUrl && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <File size={16} className="text-gray-500" />
            <span className="text-sm text-gray-700">
              {type === 'image' ? 'Изображение' : 'Видео'} загружено
            </span>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload; 